/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Box, useMultiStyleConfig} from '@chakra-ui/react'
import {useForm} from 'react-hook-form'
import PromoCodeFields from '../../../components/forms/promo-code-fields'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'

export const usePromoCode = () => {
    const {formatMessage} = useIntl()
    const {data: basket} = useCurrentBasket()
    const form = useForm()

    const applyPromoCodeMutation = useShopperBasketsMutation('addCouponToBasket')
    const removePromoCodeMutation = useShopperBasketsMutation('removeCouponFromBasket')

    const submitPromoCode = async ({code}) => {
        try {
            await applyPromoCodeMutation.mutateAsync({
                parameters: {basketId: basket?.basketId},
                body: {
                    code,
                },
            })

            form.reset({code: ''})
        } catch (e) {
            form.setError(
                'code',
                {
                    type: 'manual',
                    message: formatMessage({
                        defaultMessage:
                            'Check the code and try again, it may already be applied or the promo has expired.',
                        id: 'use_promocode.error.check_the_code',
                    }),
                },
                {shouldFocus: true},
            )
        }
    }

    const removePromoCode = async (couponItemId) => {
        removePromoCodeMutation.mutate({
            parameters: {basketId: basket?.basketId, couponItemId},
        })
    }

    return {form, submitPromoCode, removePromoCode}
}

export const PromoCode = ({form, submitPromoCode}) => {
    const style = useMultiStyleConfig('OrderSummary', {})
    const {promo} = style

    return (
        <Box
            {...promo?.promoContainer}
            data-testid="promo-code-form"
            as="form"
            onSubmit={form.handleSubmit(submitPromoCode)}
        >
            <PromoCodeFields form={form} />
        </Box>
    )
}

PromoCode.propTypes = {
    /** The form object returned from `usePromoCode` hook */
    form: PropTypes.object.isRequired,

    /** The submit callback returned from `usePromoCode` hook */
    submitPromoCode: PropTypes.func.isRequired,

    /** Props applied to inner AccordionItem. Useful for style overrides. */
    itemProps: PropTypes.object,

    promoStyle: PropTypes.object,
}

export default PromoCode
